import React, { useState } from 'react';
import { SaveIcon, RotateCcwIcon, ShieldIcon, SlidersIcon, BellIcon, DatabaseIcon, CheckCircleIcon, AlertTriangleIcon, InfoIcon } from 'lucide-react';
import { Button } from '../../ui/Button';
import { loadGovernanceConfig, saveGovernanceConfig, resetGovernanceConfig, type GovernanceConfigOverrides } from '../../../governance/pipeline/governanceConfigStore';
interface ToggleProps {
  label: string;
  description: string;
  value: boolean;
  onChange: (v: boolean) => void;
}
function Toggle({
  label,
  description,
  value,
  onChange
}: ToggleProps) {
  return <div className="flex items-start justify-between gap-4 py-4 border-b border-gray-50 last:border-0" data-id="element-3368">
      <div className="min-w-0" data-id="element-3369">
        <p className="text-sm font-medium text-gray-800" data-id="element-3370">{label}</p>
        <p className="text-xs text-gray-500 mt-0.5" data-id="element-3371">{description}</p>
      </div>
      <button role="switch" aria-checked={value} onClick={() => onChange(!value)} className={`relative flex-shrink-0 h-6 w-11 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300 ${value ? 'bg-blue-500' : 'bg-gray-200'}`} data-id="element-3372">
        <span className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${value ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-3373" />
      </button>
    </div>;
}
interface SliderFieldProps {
  label: string;
  description: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (v: number) => void;
  unit?: string;
  colorFn?: (v: number) => string;
}
function SliderField({
  label,
  description,
  value,
  min,
  max,
  step = 1,
  onChange,
  unit = '',
  colorFn
}: SliderFieldProps) {
  const pct = (value - min) / (max - min) * 100;
  const color = colorFn ? colorFn(value) : 'text-blue-600';
  return <div className="py-4 border-b border-gray-50 last:border-0" data-id="element-3374">
      <div className="flex items-center justify-between mb-1" data-id="element-3375">
        <p className="text-sm font-medium text-gray-800" data-id="element-3376">{label}</p>
        <span className={`text-sm font-bold ${color}`} data-id="element-3377">
          {value}
          {unit}
        </span>
      </div>
      <p className="text-xs text-gray-500 mb-3" data-id="element-3378">{description}</p>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e => onChange(Number(e.target.value))} className="w-full h-2 rounded-full appearance-none cursor-pointer bg-gray-200 accent-blue-500" data-id="element-3379" />
      <div className="flex justify-between mt-1" data-id="element-3380">
        <span className="text-xs text-gray-400" data-id="element-3381">
          {min}
          {unit}
        </span>
        <span className="text-xs text-gray-400" data-id="element-3382">
          {max}
          {unit}
        </span>
      </div>
    </div>;
}
interface NumberFieldProps {
  label: string;
  description: string;
  value: number;
  min?: number;
  max?: number;
  onChange: (v: number) => void;
  unit?: string;
}
function NumberField({
  label,
  description,
  value,
  min,
  max,
  onChange,
  unit
}: NumberFieldProps) {
  return <div className="py-4 border-b border-gray-50 last:border-0" data-id="element-3383">
      <div className="flex items-start justify-between gap-4" data-id="element-3384">
        <div className="min-w-0" data-id="element-3385">
          <p className="text-sm font-medium text-gray-800" data-id="element-3386">{label}</p>
          <p className="text-xs text-gray-500 mt-0.5" data-id="element-3387">{description}</p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0" data-id="element-3388">
          <input type="number" value={value} min={min} max={max} onChange={e => onChange(Number(e.target.value))} className="w-20 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-right focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100" data-id="element-3389" />
          {unit && <span className="text-sm text-gray-500" data-id="element-3390">{unit}</span>}
        </div>
      </div>
    </div>;
}
type SectionId = 'kwaliteit' | 'pipeline' | 'beleid' | 'meldingen' | 'systeem';
export function GovernanceSettingsTab() {
  const [activeSection, setActiveSection] = useState<SectionId>('kwaliteit');
  const [saved, setSaved] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  // Load persisted config on mount
  const [config, setConfig] = useState<GovernanceConfigOverrides>(() => loadGovernanceConfig());
  // Quality Gate Settings — derived from persisted config
  const [minCogLevel, setMinCogLevel] = useState(config.qualityGates?.minCogLevel ?? 2);
  const [maxCogLevel, setMaxCogLevel] = useState(config.qualityGates?.maxCogLevel ?? 5);
  const [minDidacticDepth, setMinDidacticDepth] = useState(config.qualityGates?.minDidacticDepth ?? 2);
  const [minQualityScore, setMinQualityScore] = useState(config.qualityGates?.minQualityScore ?? 70);
  // Pipeline Settings
  const [schemaEnabled, setSchemaEnabled] = useState(config.pipelineStages?.schemaEnabled ?? true);
  const [typeEnabled, setTypeEnabled] = useState(config.pipelineStages?.typeEnabled ?? true);
  const [contentEnabled, setContentEnabled] = useState(config.pipelineStages?.contentEnabled ?? true);
  const [supportEnabled, setSupportEnabled] = useState(config.pipelineStages?.supportEnabled ?? true);
  const [visualEnabled, setVisualEnabled] = useState(config.pipelineStages?.visualEnabled ?? true);
  const [graphEnabled, setGraphEnabled] = useState(config.pipelineStages?.graphEnabled ?? true);
  const [strictMode, setStrictMode] = useState(config.pipelineBehavior?.strictMode ?? false);
  const [autoBlock, setAutoBlock] = useState(config.pipelineBehavior?.autoBlock ?? true);
  // Policy Settings
  const [minQuestionsPerSkill, setMinQuestionsPerSkill] = useState(config.policy?.minQuestionsPerSkill ?? 5);
  const [maxQuestionsPerSkill, setMaxQuestionsPerSkill] = useState(config.policy?.maxQuestionsPerSkill ?? 50);
  const [requireHints, setRequireHints] = useState(config.policy?.requireHints ?? true);
  const [requireExplanation, setRequireExplanation] = useState(config.policy?.requireExplanation ?? true);
  const [requireVisuals, setRequireVisuals] = useState(config.policy?.requireVisuals ?? false);
  const [distributionCheck, setDistributionCheck] = useState(config.policy?.distributionCheck ?? true);
  // Notification Settings
  const [notifyBlockers, setNotifyBlockers] = useState(config.notifications?.notifyBlockers ?? true);
  const [notifyWarnings, setNotifyWarnings] = useState(config.notifications?.notifyWarnings ?? false);
  const [notifyWeeklyReport, setNotifyWeeklyReport] = useState(config.notifications?.notifyWeeklyReport ?? true);
  const [notifyNewRules, setNotifyNewRules] = useState(config.notifications?.notifyNewRules ?? true);
  // System Settings
  const [cacheEnabled, setCacheEnabled] = useState(config.system?.cacheEnabled ?? true);
  const [debugMode, setDebugMode] = useState(config.system?.debugMode ?? false);
  const [autoValidateOnSave, setAutoValidateOnSave] = useState(config.system?.autoValidateOnSave ?? true);
  const [batchSize, setBatchSize] = useState(config.system?.batchSize ?? 100);
  const markChanged = () => {
    setHasChanges(true);
    setSaved(false);
  };
  const handleSave = () => {
    // Build config from current state and persist
    const newConfig: GovernanceConfigOverrides = {
      version: 1,
      qualityGates: {
        minCogLevel,
        maxCogLevel,
        minDidacticDepth,
        minQualityScore
      },
      pipelineStages: {
        schemaEnabled,
        typeEnabled,
        contentEnabled,
        supportEnabled,
        visualEnabled,
        graphEnabled
      },
      pipelineBehavior: {
        strictMode,
        autoBlock
      },
      policy: {
        minQuestionsPerSkill,
        maxQuestionsPerSkill,
        requireHints,
        requireExplanation,
        requireVisuals,
        distributionCheck
      },
      notifications: {
        notifyBlockers,
        notifyWarnings,
        notifyWeeklyReport,
        notifyNewRules
      },
      system: {
        cacheEnabled,
        debugMode,
        autoValidateOnSave,
        batchSize
      }
    };
    const success = saveGovernanceConfig(newConfig);
    if (success) {
      setConfig(newConfig);
      setSaved(true);
      setHasChanges(false);
      setTimeout(() => setSaved(false), 3000);
    }
  };
  const handleReset = () => {
    const defaults = resetGovernanceConfig();
    setConfig(defaults);
    setMinCogLevel(defaults.qualityGates?.minCogLevel ?? 2);
    setMaxCogLevel(defaults.qualityGates?.maxCogLevel ?? 5);
    setMinDidacticDepth(defaults.qualityGates?.minDidacticDepth ?? 2);
    setMinQualityScore(defaults.qualityGates?.minQualityScore ?? 70);
    setSchemaEnabled(defaults.pipelineStages?.schemaEnabled ?? true);
    setTypeEnabled(defaults.pipelineStages?.typeEnabled ?? true);
    setContentEnabled(defaults.pipelineStages?.contentEnabled ?? true);
    setSupportEnabled(defaults.pipelineStages?.supportEnabled ?? true);
    setVisualEnabled(defaults.pipelineStages?.visualEnabled ?? true);
    setGraphEnabled(defaults.pipelineStages?.graphEnabled ?? true);
    setStrictMode(defaults.pipelineBehavior?.strictMode ?? false);
    setAutoBlock(defaults.pipelineBehavior?.autoBlock ?? true);
    setMinQuestionsPerSkill(defaults.policy?.minQuestionsPerSkill ?? 5);
    setMaxQuestionsPerSkill(defaults.policy?.maxQuestionsPerSkill ?? 50);
    setRequireHints(defaults.policy?.requireHints ?? true);
    setRequireExplanation(defaults.policy?.requireExplanation ?? true);
    setRequireVisuals(defaults.policy?.requireVisuals ?? false);
    setDistributionCheck(defaults.policy?.distributionCheck ?? true);
    setHasChanges(false);
  };
  const sections: {
    id: SectionId;
    label: string;
    icon: React.ReactNode;
  }[] = [{
    id: 'kwaliteit',
    label: 'Kwaliteitspoorten',
    icon: <ShieldIcon className="h-4 w-4" data-id="element-3391" />
  }, {
    id: 'pipeline',
    label: 'Pipeline',
    icon: <SlidersIcon className="h-4 w-4" data-id="element-3392" />
  }, {
    id: 'beleid',
    label: 'Beleid',
    icon: <CheckCircleIcon className="h-4 w-4" data-id="element-3393" />
  }, {
    id: 'meldingen',
    label: 'Meldingen',
    icon: <BellIcon className="h-4 w-4" data-id="element-3394" />
  }, {
    id: 'systeem',
    label: 'Systeem',
    icon: <DatabaseIcon className="h-4 w-4" data-id="element-3395" />
  }];
  const cogLevelNames: Record<number, string> = {
    1: 'Onthouden',
    2: 'Begrijpen',
    3: 'Toepassen',
    4: 'Analyseren',
    5: 'Evalueren',
    6: 'Creëren'
  };
  const didacticNames: Record<number, string> = {
    1: 'Oppervlakkig',
    2: 'Procedureel',
    3: 'Conceptueel',
    4: 'Analytisch',
    5: 'Creatief'
  };
  return <div className="space-y-5" data-id="element-3396">
      {/* Disclaimer Banner — Updated to reflect persistence */}
      <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3" data-id="element-3397">
        <CheckCircleIcon className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" data-id="element-3398" />
        <div data-id="element-3399">
          <p className="text-sm font-semibold text-emerald-800" data-id="element-3400">
            Instellingen worden lokaal opgeslagen
          </p>
          <p className="text-xs text-emerald-700 mt-0.5 leading-relaxed" data-id="element-3401">
            Wijzigingen worden opgeslagen in je browser en blijven behouden na
            het herladen van de pagina.
            {config.lastSaved && <span className="ml-1 text-emerald-600" data-id="element-3402">
                Laatst opgeslagen:{' '}
                {new Date(config.lastSaved).toLocaleString('nl-NL')}
              </span>}
          </p>
        </div>
      </div>

      {/* Save Bar */}
      {(hasChanges || saved) && <div className={`flex items-center justify-between rounded-xl border px-4 py-3 ${saved ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`} data-id="element-3403">
          <div className="flex items-center gap-2" data-id="element-3404">
            {saved ? <CheckCircleIcon className="h-4 w-4 text-emerald-600" data-id="element-3405" /> : <AlertTriangleIcon className="h-4 w-4 text-amber-600" data-id="element-3406" />}
            <p className={`text-sm font-medium ${saved ? 'text-emerald-700' : 'text-amber-700'}`} data-id="element-3407">
              {saved ? 'Instellingen opgeslagen!' : 'Je hebt niet-opgeslagen wijzigingen'}
            </p>
          </div>
          {!saved && <div className="flex gap-2" data-id="element-3408">
              <Button variant="ghost" size="sm" onClick={handleReset} data-id="element-3409">
                <RotateCcwIcon className="h-3.5 w-3.5 mr-1" data-id="element-3410" />
                Herstel
              </Button>
              <Button variant="primary" size="sm" onClick={handleSave} data-id="element-3411">
                <SaveIcon className="h-3.5 w-3.5 mr-1" data-id="element-3412" />
                Opslaan
              </Button>
            </div>}
        </div>}

      <div className="flex flex-col gap-5 lg:flex-row" data-id="element-3413">
        {/* Sidebar Navigation */}
        <div className="lg:w-48 flex-shrink-0" data-id="element-3414">
          <nav className="space-y-1" data-id="element-3415">
            {sections.map(section => <button key={section.id} onClick={() => setActiveSection(section.id)} className={`flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${activeSection === section.id ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'}`} data-id="element-3416">
                <span className={activeSection === section.id ? 'text-blue-600' : 'text-gray-400'} data-id="element-3417">
                  {section.icon}
                </span>
                {section.label}
              </button>)}
          </nav>
        </div>

        {/* Settings Panel */}
        <div className="flex-1 min-w-0" data-id="element-3418">
          <div className="rounded-xl border border-gray-100 bg-white p-5" data-id="element-3419">
            {/* Kwaliteitspoorten */}
            {activeSection === 'kwaliteit' && <div data-id="element-3420">
                <h2 className="text-base font-bold text-gray-900 mb-1" data-id="element-3421">
                  Kwaliteitspoorten
                </h2>
                <p className="text-sm text-gray-500 mb-5" data-id="element-3422">
                  Stel de minimale kwaliteitseisen in waaraan vragen moeten
                  voldoen.
                </p>
                <SliderField label="Minimaal cognitief niveau" description={`Vragen onder niveau ${minCogLevel} (${cogLevelNames[minCogLevel]}) worden gemarkeerd`} value={minCogLevel} min={1} max={6} onChange={v => {
              setMinCogLevel(v);
              markChanged();
            }} colorFn={v => v <= 2 ? 'text-amber-600' : 'text-blue-600'} data-id="element-3423" />
                <SliderField label="Maximaal cognitief niveau" description={`Vragen boven niveau ${maxCogLevel} (${cogLevelNames[maxCogLevel]}) worden gemarkeerd`} value={maxCogLevel} min={1} max={6} onChange={v => {
              setMaxCogLevel(v);
              markChanged();
            }} colorFn={v => v >= 5 ? 'text-emerald-600' : 'text-blue-600'} data-id="element-3424" />
                <SliderField label="Minimale didactische diepte" description={`Vragen onder diepte ${minDidacticDepth} (${didacticNames[minDidacticDepth]}) worden gemarkeerd`} value={minDidacticDepth} min={1} max={5} onChange={v => {
              setMinDidacticDepth(v);
              markChanged();
            }} data-id="element-3425" />
                <SliderField label="Minimale kwaliteitsscore" description="Vragen onder deze drempel worden als onvoldoende beschouwd" value={minQualityScore} min={0} max={100} step={5} onChange={v => {
              setMinQualityScore(v);
              markChanged();
            }} unit="%" colorFn={v => v >= 80 ? 'text-emerald-600' : v >= 60 ? 'text-amber-600' : 'text-red-600'} data-id="element-3426" />
              </div>}

            {/* Pipeline */}
            {activeSection === 'pipeline' && <div data-id="element-3427">
                <h2 className="text-base font-bold text-gray-900 mb-1" data-id="element-3428">
                  Pipeline Instellingen
                </h2>
                <p className="text-sm text-gray-500 mb-5" data-id="element-3429">
                  Schakel validatiefasen in of uit en configureer het
                  pipeline-gedrag.
                </p>
                <Toggle label="Schema Validatie" description="Controleer JSON-structuur en verplichte velden" value={schemaEnabled} onChange={v => {
              setSchemaEnabled(v);
              markChanged();
            }} data-id="element-3430" />
                <Toggle label="Vraagtype Validatie" description="Controleer vraagtype-specifieke regels" value={typeEnabled} onChange={v => {
              setTypeEnabled(v);
              markChanged();
            }} data-id="element-3431" />
                <Toggle label="Inhoud & Taalvalidatie" description="Controleer didactische kwaliteit en taalgebruik" value={contentEnabled} onChange={v => {
              setContentEnabled(v);
              markChanged();
            }} data-id="element-3432" />
                <Toggle label="Hint & Uitleg Validatie" description="Controleer aanwezigheid van ondersteunend materiaal" value={supportEnabled} onChange={v => {
              setSupportEnabled(v);
              markChanged();
            }} data-id="element-3433" />
                <Toggle label="Visuele Hulpmiddelen Validatie" description="Controleer visuele componenten en antwoordlekken" value={visualEnabled} onChange={v => {
              setVisualEnabled(v);
              markChanged();
            }} data-id="element-3434" />
                <Toggle label="LDG-Graaf Validatie" description="Controleer leerpad-afhankelijkheden" value={graphEnabled} onChange={v => {
              setGraphEnabled(v);
              markChanged();
            }} data-id="element-3435" />
                <div className="mt-4 pt-4 border-t border-gray-100" data-id="element-3436">
                  <Toggle label="Strikte modus" description="Behandel waarschuwingen als blokkerende fouten" value={strictMode} onChange={v => {
                setStrictMode(v);
                markChanged();
              }} data-id="element-3437" />
                  <Toggle label="Automatisch blokkeren" description="Blokkeer vragen automatisch bij BLOCKER-fouten" value={autoBlock} onChange={v => {
                setAutoBlock(v);
                markChanged();
              }} data-id="element-3438" />
                </div>
              </div>}

            {/* Beleid */}
            {activeSection === 'beleid' && <div data-id="element-3439">
                <h2 className="text-base font-bold text-gray-900 mb-1" data-id="element-3440">
                  Beleidsregels
                </h2>
                <p className="text-sm text-gray-500 mb-5" data-id="element-3441">
                  Configureer de beleidsregels voor vraagsets en
                  vaardigheidsdekking.
                </p>
                <NumberField label="Minimaal aantal vragen per vaardigheid" description="Vaardigheden met minder vragen krijgen een waarschuwing" value={minQuestionsPerSkill} min={1} max={20} onChange={v => {
              setMinQuestionsPerSkill(v);
              markChanged();
            }} unit="vragen" data-id="element-3442" />
                <NumberField label="Maximaal aantal vragen per vaardigheid" description="Vaardigheden met meer vragen krijgen een informatieve melding" value={maxQuestionsPerSkill} min={10} max={200} onChange={v => {
              setMaxQuestionsPerSkill(v);
              markChanged();
            }} unit="vragen" data-id="element-3443" />
                <Toggle label="Hints verplicht" description="Vragen zonder hint krijgen een waarschuwing" value={requireHints} onChange={v => {
              setRequireHints(v);
              markChanged();
            }} data-id="element-3444" />
                <Toggle label="Uitleg verplicht" description="Vragen zonder uitleg krijgen een waarschuwing" value={requireExplanation} onChange={v => {
              setRequireExplanation(v);
              markChanged();
            }} data-id="element-3445" />
                <Toggle label="Visuele hulpmiddelen verplicht" description="Vragen zonder visueel hulpmiddel krijgen een waarschuwing" value={requireVisuals} onChange={v => {
              setRequireVisuals(v);
              markChanged();
            }} data-id="element-3446" />
                <Toggle label="Cognitief niveau distributie controleren" description="Controleer of vaardigheden een gevarieerde verdeling hebben" value={distributionCheck} onChange={v => {
              setDistributionCheck(v);
              markChanged();
            }} data-id="element-3447" />
              </div>}

            {/* Meldingen */}
            {activeSection === 'meldingen' && <div data-id="element-3448">
                <h2 className="text-base font-bold text-gray-900 mb-1" data-id="element-3449">
                  Meldingsinstellingen
                </h2>
                <p className="text-sm text-gray-500 mb-5" data-id="element-3450">
                  Kies wanneer je meldingen wilt ontvangen over
                  governance-activiteit.
                </p>
                <Toggle label="Meldingen bij blokkerende fouten" description="Ontvang een melding wanneer een vraag wordt geblokkeerd" value={notifyBlockers} onChange={v => {
              setNotifyBlockers(v);
              markChanged();
            }} data-id="element-3451" />
                <Toggle label="Meldingen bij waarschuwingen" description="Ontvang een melding bij elke nieuwe waarschuwing" value={notifyWarnings} onChange={v => {
              setNotifyWarnings(v);
              markChanged();
            }} data-id="element-3452" />
                <Toggle label="Wekelijks kwaliteitsrapport" description="Ontvang elke week een samenvatting van de kwaliteitsmetingen" value={notifyWeeklyReport} onChange={v => {
              setNotifyWeeklyReport(v);
              markChanged();
            }} data-id="element-3453" />
                <Toggle label="Meldingen bij nieuwe regels" description="Ontvang een melding wanneer nieuwe governance-regels worden toegevoegd" value={notifyNewRules} onChange={v => {
              setNotifyNewRules(v);
              markChanged();
            }} data-id="element-3454" />
              </div>}

            {/* Systeem */}
            {activeSection === 'systeem' && <div data-id="element-3455">
                <h2 className="text-base font-bold text-gray-900 mb-1" data-id="element-3456">
                  Systeeminstellingen
                </h2>
                <p className="text-sm text-gray-500 mb-5" data-id="element-3457">
                  Technische instellingen voor het governance-systeem.
                </p>
                <Toggle label="Cache inschakelen" description="Sla validatieresultaten op in cache voor snellere verwerking" value={cacheEnabled} onChange={v => {
              setCacheEnabled(v);
              markChanged();
            }} data-id="element-3458" />
                <Toggle label="Automatisch valideren bij opslaan" description="Voer validatie automatisch uit wanneer een vraag wordt opgeslagen" value={autoValidateOnSave} onChange={v => {
              setAutoValidateOnSave(v);
              markChanged();
            }} data-id="element-3459" />
                <Toggle label="Foutopsporingsmodus" description="Toon uitgebreide foutinformatie in de console" value={debugMode} onChange={v => {
              setDebugMode(v);
              markChanged();
            }} data-id="element-3460" />
                <NumberField label="Batch-grootte voor validatie" description="Aantal vragen dat tegelijk wordt gevalideerd" value={batchSize} min={10} max={500} onChange={v => {
              setBatchSize(v);
              markChanged();
            }} unit="vragen" data-id="element-3461" />

                <div className="mt-6 pt-4 border-t border-gray-100" data-id="element-3462">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3" data-id="element-3463">
                    Gevaarlijke Acties
                  </h3>
                  <div className="space-y-2" data-id="element-3464">
                    <Button variant="secondary" size="sm" className="w-full justify-start" data-id="element-3465">
                      <RotateCcwIcon className="h-4 w-4 mr-2" data-id="element-3466" />
                      Cache leegmaken
                    </Button>
                    <Button variant="danger" size="sm" className="w-full justify-start" data-id="element-3467">
                      Alle instellingen terugzetten naar standaard
                    </Button>
                  </div>
                </div>
              </div>}
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-3 mt-4" data-id="element-3468">
            <Button variant="ghost" size="sm" onClick={handleReset} disabled={!hasChanges} data-id="element-3469">
              <RotateCcwIcon className="h-4 w-4 mr-1.5" data-id="element-3470" />
              Herstel
            </Button>
            <Button variant="primary" size="sm" onClick={handleSave} disabled={!hasChanges} data-id="element-3471">
              <SaveIcon className="h-4 w-4 mr-1.5" data-id="element-3472" />
              Wijzigingen Opslaan
            </Button>
          </div>
        </div>
      </div>
    </div>;
}